import { capitalize } from "lodash";
import type { AchievementReportNode, AchievementReportRow, SectionLookup } from "./types";

export const DEFAULT_ACHIEVEMENT_TYPE_CODE = "ADM";
export const DEFAULT_ACHIEVEMENT_TYPE_TITLE = "American Development Model";

type AchievementTypeMatch = {
  title: string;
  ccmsAttributeType?: string | null;
  codes?: Array<{ code?: string | null }>;
};

function normalizeAchievementCode(value: string | null | undefined): string {
  return value?.trim().toUpperCase() ?? "";
}

export function findDefaultAchievementType<T extends AchievementTypeMatch>(types: T[] = []): T | undefined {
  const byTitle = types.find((type) => type.title.toLowerCase().includes(DEFAULT_ACHIEVEMENT_TYPE_TITLE.toLowerCase()));
  if (byTitle) return byTitle;

  return types.find((type) => {
    if (normalizeAchievementCode(type.ccmsAttributeType) === DEFAULT_ACHIEVEMENT_TYPE_CODE) {
      return true;
    }

    return type.codes?.some((code) => normalizeAchievementCode(code.code) === DEFAULT_ACHIEVEMENT_TYPE_CODE);
  });
}

export function buildAchievementReportRowId(row: AchievementReportNode): string {
  return [
    row.pgaId,
    row.recognizedAt ?? "",
    row.title ?? "",
    row.recognitionYear ?? "",
    row.reportingEntity ?? ""
  ].join("|");
}

export function mapAchievementReportRows(nodes: AchievementReportNode[] = []): AchievementReportRow[] {
  return nodes.map((node) => ({
    ...node,
    id: buildAchievementReportRowId(node)
  }));
}

export function hasNestedAchievementFields(row: AchievementReportNode): boolean {
  return !!(row.recognitionYear || row.reportingEntity || row.expiresOn || row.remarks);
}

export function buildSectionLookup(
  sections: { id: string; legacyId?: string | null; name: string }[] = []
): SectionLookup {
  const byId = new Map<string, string>();
  const byLegacyId = new Map<string, string>();

  sections.forEach((section) => {
    byId.set(section.id, section.name);
    if (section.legacyId) {
      byLegacyId.set(section.legacyId, section.name);
    }
  });

  return { byId, byLegacyId };
}

export function resolveSectionName(sectionId: string | null | undefined, lookup: SectionLookup): string | undefined {
  if (!sectionId) return undefined;
  return lookup.byId.get(sectionId) ?? lookup.byLegacyId.get(sectionId);
}

export function formatMemberStatus(status: string | null | undefined): string | null {
  if (!status || status.toLowerCase() === "unknown") {
    return null;
  }

  return capitalize(status);
}
